import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { postResetPassword } from "../../shared/passwordResetApi";

const MIN_PASSWORD = 5;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token")?.trim() || "";
  const email = searchParams.get("email")?.trim() || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const invalidLink = !token || !email;

  useEffect(() => {
    let t;
    if (successMessage) {
      t = setTimeout(() => navigate("/login", { replace: true }), 3000);
    }
    return () => clearTimeout(t);
  }, [successMessage, navigate]);

  const validateClient = () => {
    let ok = true;

    setPasswordError("");
    setConfirmError("");

    if (!password || password.length < MIN_PASSWORD) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD} characters.`);
      ok = false;
    }
    if (password !== confirm) {
      setConfirmError("Passwords do not match.");
      ok = false;
    }
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError("");
    setSuccessMessage("");

    if (invalidLink) return;
    if (!validateClient()) return;

    setLoading(true);

    let emailForApi = email;
    try {
      emailForApi = decodeURIComponent(email);
    } catch {
      emailForApi = email;
    }

    const result = await postResetPassword({
      email: emailForApi,
      token,
      password,
      password_confirmation: confirm,
    });
    setLoading(false);

    if (result.ok && result.status === 200) {
      setSuccessMessage(
        result.data?.message ||
          "Your password has been reset. You can sign in with your new password."
      );
      return;
    }

    const data = result.data || {};
    const status = result.status;

    if (status === 422 && data.errors) {
      const errs = data.errors;
      const pErr = errs.password?.[0];
      const cErr = errs.password_confirmation?.[0];
      if (pErr) setPasswordError(pErr);
      if (cErr) setConfirmError(cErr);
      if (data.message && !pErr && !cErr) {
        setGlobalError(data.message);
      }
      return;
    }

    if (status === 429) {
      setGlobalError(data.message || "Please wait before retrying.");
      return;
    }

    const msg =
      data.message ||
      "Password reset could not be completed. Please request a new link.";
    setGlobalError(msg);
  };

  let emailDisplay = email;
  if (emailDisplay) {
    try {
      emailDisplay = decodeURIComponent(emailDisplay);
    } catch {
      /* keep as-is */
    }
  }

  return (
    <div className="h-full">
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-5 text-center text-3xl font-bold leading-9 tracking-tight text-gray-900">
            Flux Orders
          </h2>
          <h2 className="mt-5 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Set new password
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {invalidLink ? (
            <div className="space-y-4">
              <p className="text-center text-sm text-red-700">
                Invalid or incomplete reset link. Please request a new one from
                the forgot password page.
              </p>
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Forgot password
                </Link>
              </div>
              <div className="text-center text-sm">
                <Link
                  to="/login"
                  className="font-semibold text-gray-600 hover:text-gray-500"
                >
                  Back to login
                </Link>
              </div>
            </div>
          ) : successMessage ? (
            <div className="space-y-4">
              <div className="rounded-md bg-green-50 p-4 text-sm text-green-800">
                {successMessage}
              </div>
              <p className="text-center text-sm text-gray-600">
                Redirecting to login…
              </p>
              <div className="text-center">
                <Link
                  to="/login"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Go to login now
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Email
                </label>
                <p className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                  {emailDisplay}
                </p>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  New password
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    disabled={loading}
                    className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                  />
                  {passwordError ? (
                    <p className="mt-1 text-sm text-red-500">{passwordError}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      Minimum {MIN_PASSWORD} characters.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="password_confirmation"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Confirm password
                </label>
                <div className="mt-2">
                  <input
                    id="password_confirmation"
                    name="password_confirmation"
                    type="password"
                    autoComplete="new-password"
                    disabled={loading}
                    className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={confirm}
                    onChange={(ev) => setConfirm(ev.target.value)}
                  />
                  {confirmError ? (
                    <p className="mt-1 text-sm text-red-500">{confirmError}</p>
                  ) : null}
                </div>
              </div>

              {globalError ? (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                  {globalError}
                  {(globalError.toLowerCase().includes("invalid") ||
                    globalError.toLowerCase().includes("expired")) && (
                    <div className="mt-3">
                      <Link
                        to="/forgot-password"
                        className="font-semibold underline"
                      >
                        Request a new link
                      </Link>
                    </div>
                  )}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center rounded-md bg-main-purple px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
              >
                {loading ? "Saving…" : "Reset password"}
              </button>
            </form>
          )}

          {!invalidLink && !successMessage ? (
            <div className="mt-6 text-center text-sm">
              <Link
                to="/login"
                className="font-semibold text-gray-600 hover:text-gray-500"
              >
                Back to login
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
